import { Form, message, Modal, Select, Button, Flex, Divider, Table, theme } from 'antd';
import { FC } from 'react';
import { TransferRow } from './TransfersPage';
import { useInitiateTransferWithdrawalStage } from './useInitiateTransferWithdrawalStage';
import { useTransfer } from './useTransfer';
import Column from 'antd/es/table/Column';
import { upperCaseFirstLetter } from '../libs/string';
import { CopyOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';

export const InitiateTransferWithdrawalStageModal: FC<{ transfer: TransferRow; onClose: () => void }> = ({
  transfer,
  onClose,
}) => {
  const { token } = theme.useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const query = useTransfer(transfer?.id, transfer?.type);
  const { initiateTransferWithdrawalStage, isInitiatingTransferWithdrawalStage } = useInitiateTransferWithdrawalStage();

  const [form] = Form.useForm();

  const onFinish = (formData: {
    'offramp.source_instructions.crypto.network': string;
    'offramp.destination_instructions.transfer_purpose': string;
    'offramp.source_instructions.fiat.bank_account_type': string;
  }) => {
    initiateTransferWithdrawalStage({
      transfer_id: transfer.id,
      transfer_type: transfer.type,
      offramp: {
        source_instructions: {
          crypto: {
            network: formData['offramp.source_instructions.crypto.network'],
          },
          fiat: {
            bank_account_type: formData['offramp.source_instructions.fiat.bank_account_type'],
          },
        },
        destination_instructions: {
          transfer_purpose: formData['offramp.destination_instructions.transfer_purpose'],
        },
      },
    })
      .then(() => {
        messageApi.success('Successfully initiated!');
        onClose();
      })
      .catch(() => {
        messageApi.error('Something is wrong');
      });
  };

  const renderDetails = () => {
    if (!transfer) {
      return null;
    }

    const dataSource = [
      {
        key: 'Customer ID',
        value: transfer.customer_id,
      },
      {
        key: 'Customer Name',
        value:
          transfer.customer_beneficiary_type === 'corporate'
            ? transfer.customer_company_name
            : `${transfer.customer_first_name} ${transfer.customer_last_name}`,
      },
      {
        key: 'Amount',
        value: Number(transfer.requested_amount).toLocaleString(),
      },
      {
        key: 'Source payment rail',
        value: upperCaseFirstLetter(transfer?.source_network),
      },
      {
        key: 'Source currency',
        value: transfer.source_currency.toUpperCase(),
      },
      {
        key: 'Destination payment rail',
        value: upperCaseFirstLetter(transfer.destination_network),
      },
      {
        key: 'Destination currency',
        value: transfer?.destination_currency.toUpperCase(),
      },
    ];

    if (query.data?.offramp_transfer && Object.keys(query.data?.offramp_transfer).length) {
      dataSource.push(
        {
          key: 'Bank name',
          value: query.data.offramp_transfer.destination_bank_account.bank_name,
        },
        {
          key: 'Account holder type',
          value: upperCaseFirstLetter(query.data.offramp_transfer.destination_bank_account.account_holder_type),
        },
        {
          key: 'Account holder',
          value: query.data.offramp_transfer.destination_bank_account.account_holder_business_name
            ? query.data.offramp_transfer.destination_bank_account.account_holder_business_name
            : `${query.data.offramp_transfer.destination_bank_account.account_holder_first_name} ${query.data.offramp_transfer.destination_bank_account.account_holder_last_name}`,
        }
      );

      if (query.data.offramp_transfer.destination_bank_account.iban) {
        dataSource.push(
          {
            key: 'IBAN',
            value: query.data.offramp_transfer.destination_bank_account.iban.iban_number!,
          },
          {
            key: 'BIC code',
            value: query.data.offramp_transfer.destination_bank_account.iban.bic!,
          }
        );
      } else if (query.data.offramp_transfer.destination_bank_account.us) {
        dataSource.push(
          {
            key: 'Account number',
            value: query.data.offramp_transfer.destination_bank_account.us.account_number!,
          },
          {
            key: 'Routing number',
            value: query.data.offramp_transfer.destination_bank_account.us.routing_number!,
          }
        );
      } else if (query.data.offramp_transfer.destination_bank_account.international_wire) {
        dataSource.push(
          {
            key: 'Account number',
            value: query.data.offramp_transfer.destination_bank_account.international_wire.account_number!,
          },
          {
            key: 'Routing number',
            value: query.data.offramp_transfer.destination_bank_account.international_wire.routing_number || 'N/A',
          },
          {
            key: 'BIC code',
            value: query.data.offramp_transfer.destination_bank_account.international_wire.bic,
          }
        );
      }

      dataSource.push(
        {
          key: 'Account holder address',
          value: `${query.data.offramp_transfer.destination_bank_account.account_holder_address.street_line_1}, ${query.data.offramp_transfer.destination_bank_account.account_holder_address.city}, ${query.data.offramp_transfer.destination_bank_account.account_holder_address.country}, ${query.data.offramp_transfer.destination_bank_account.account_holder_address.postal_code}`,
        },
        {
          key: 'Transfer Purpose',
          value: query.data.offramp_transfer.transfer_purpose || 'N/A',
        }
      );
    }

    return (
      <Table
        size="small"
        style={{ marginBottom: '32px' }}
        showHeader={false}
        rowHoverable={false}
        pagination={false}
        dataSource={dataSource}
      >
        <Column
          key="key"
          width={250}
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            const subHeaderStyles = css`
              color: ${token.colorTextTertiary};
            `;
            if (
              [
                'IBAN',
                'BIC code',
                'Account number',
                'Routing number',
                'Account holder',
                'Account holder address',
              ].includes(v.key)
            ) {
              return (
                <Flex align="center" gap={'small'}>
                  {v.value}
                  <Button
                    type="text"
                    className={subHeaderStyles}
                    size="small"
                    icon={<CopyOutlined width={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(v.value);
                      messageApi.success('Copied to clipboard');
                    }}
                  />
                </Flex>
              );
            }

            return v.value;
          }}
        />
      </Table>
    );
  };

  return (
    <Modal
      loading={isInitiatingTransferWithdrawalStage || query.isLoading}
      title="Initiate Transfer Withdrawal Stage"
      destroyOnClose
      footer={null}
      width={620}
      open
      onCancel={onClose}
    >
      {contextHolder}
      <Form
        onFinish={onFinish}
        name="initiateTransferWithdrawalStageModal"
        form={form}
        requiredMark={false}
        style={{ paddingTop: '32px' }}
        layout="vertical"
        autoComplete="off"
      >
        {renderDetails()}

        <Form.Item

          shouldUpdate={true} noStyle
        >
          {() => {
            if (transfer.type === 'offramp') {
              return <>
                <Divider variant="dashed">Fiat Source Instructions</Divider>

                <Form.Item
                  name="offramp.source_instructions.fiat.bank_account_type"
                  layout="vertical"
                  label="Bank Account Type"
                >
                  <Select placeholder="Select value...">
                    <Select.Option value="align_primary_account">Align Primary Account</Select.Option>
                    <Select.Option value="customer_primary_account">Customer Primary Account</Select.Option>
                  </Select>
                </Form.Item>

                <Divider variant="dashed">Crypto Source Instructions</Divider>

                <Form.Item
                  name="offramp.source_instructions.crypto.network"
                  layout="vertical"
                  label="Network"
                >
                  <Select placeholder="Select value...">
                    <Select.Option value="solana">Solana</Select.Option>
                    <Select.Option value="base">Base</Select.Option>
                  </Select>
                </Form.Item>

                <Divider variant="dashed">Destination Instructions</Divider>

                <Form.Item name="offramp.destination_instructions.transfer_purpose" layout="vertical" label="Transfer Purpose">
                  <Select placeholder="Select value...">
                    <Select.Option value="intergroup_transfer">Intergroup Transfer</Select.Option>
                    <Select.Option value="resident_transfer_between_accounts">Resident Transfer Between Accounts</Select.Option>
                    <Select.Option value="professional">Professional</Select.Option>
                    <Select.Option value="salaries">Salaries</Select.Option>
                  </Select>
                </Form.Item>

              </>
            }

            return null
          }}
        </Form.Item>

        <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            Initiate Withdrawal
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
